import { useRef, useState } from 'react'
import type { SplitBuckets, TimeBucket } from '../../timeline'

/** `08-21 14:30`, for timeline axis and tooltips — short, local, sortable. */
export function shortTime(ms: number): string {
  const d = new Date(ms)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

/**
 * The timeline histogram: one mark per timeslice bucket, drag (or click) to
 * zoom the search to that range. Inline SVG, viewBox-scaled to the panel
 * width; the drag maps clientX back to bucket indexes, so no chart library
 * and no resize observers.
 *
 * Three faces, one interaction: single-series BARS (default) or AREA (better
 * for trend-reading over long ranges), and STACKED bars when a split is
 * active — categorical slots `--viz-1..5` + muted Other, legend below, a
 * 1px surface stroke as the spacer between stacked segments.
 */
export function TimelineChart({
  buckets,
  stack,
  sliceMs,
  style,
  onSelectRange,
}: {
  buckets: TimeBucket[]
  /** When present, stacked mode — `buckets` is ignored. */
  stack?: SplitBuckets
  sliceMs: number
  style: 'bars' | 'area'
  onSelectRange: (startMs: number, endMs: number) => void
}) {
  const [drag, setDrag] = useState<{ a: number; b: number } | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const W = 1000
  const H = 72
  const marks: Array<{ t: number; total: number; parts?: number[] }> = stack
    ? stack.data
    : buckets.map((b) => ({ t: b.t, total: b.count }))
  const n = marks.length
  const max = marks.reduce((m, b) => Math.max(m, b.total), 1)
  const bw = W / n
  const yOf = (v: number) => (v / max) * (H - 8)

  const idxAt = (clientX: number) => {
    const r = svgRef.current?.getBoundingClientRect()
    if (!r || r.width === 0) return 0
    return Math.min(n - 1, Math.max(0, Math.floor(((clientX - r.left) / r.width) * n)))
  }
  const sel = drag ? ([Math.min(drag.a, drag.b), Math.max(drag.a, drag.b)] as const) : null

  const seriesColor = (i: number) =>
    stack && stack.series[i] === 'Other' ? 'var(--viz-other)' : `var(--viz-${i + 1})`

  return (
    <div className="q-tl">
      {/*
        role="application": the svg is a pointer instrument, not a picture —
        drag selects a time range. Keyboard users reach the same ranges
        through the time controls in the builder, so no key handling here.
      */}
      <svg
        ref={svgRef}
        className="q-tl-svg"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="application"
        aria-label="Event count over time — drag to zoom the search to a range"
        onMouseDown={(e) => {
          const i = idxAt(e.clientX)
          setDrag({ a: i, b: i })
        }}
        onMouseMove={(e) => {
          if (drag) setDrag({ a: drag.a, b: idxAt(e.clientX) })
        }}
        onMouseUp={(e) => {
          if (!drag) return
          // The RELEASE position closes the range — relying on the last
          // mousemove alone loses the tail of a fast drag.
          const end = idxAt(e.clientX)
          const lo = Math.min(drag.a, end)
          const hi = Math.max(drag.a, end)
          setDrag(null)
          onSelectRange(marks[lo]!.t, marks[hi]!.t + sliceMs)
        }}
        onMouseLeave={() => setDrag(null)}
      >
        {stack ? (
          marks.map((b, i) => {
            let yBase = H - 2
            return (
              <g key={b.t}>
                {b.parts!.map((v, si) => {
                  if (v <= 0) return null
                  const h = yOf(v)
                  yBase -= h
                  return (
                    <rect
                      key={si}
                      className="q-tl-seg"
                      x={i * bw + 0.5}
                      y={yBase}
                      width={Math.max(0.5, bw - 1)}
                      height={h}
                      style={{ fill: seriesColor(si) }}
                      data-selected={sel && i >= sel[0] && i <= sel[1] ? '' : undefined}
                    >
                      <title>{`${shortTime(b.t)} · ${stack.series[si]} — ${v.toLocaleString('en-US')}`}</title>
                    </rect>
                  )
                })}
              </g>
            )
          })
        ) : style === 'area' ? (
          <>
            <polygon
              className="q-tl-area"
              points={[
                `0,${H - 2}`,
                ...marks.map((b, i) => `${i * bw + bw / 2},${H - 2 - yOf(b.total)}`),
                `${W},${H - 2}`,
              ].join(' ')}
            />
            <polyline
              className="q-tl-line"
              points={marks.map((b, i) => `${i * bw + bw / 2},${H - 2 - yOf(b.total)}`).join(' ')}
            />
            {/* Invisible per-bucket hover targets carry the tooltips. */}
            {marks.map((b, i) => (
              <rect
                key={b.t}
                className="q-tl-hover"
                x={i * bw}
                y={0}
                width={bw}
                height={H}
                data-selected={sel && i >= sel[0] && i <= sel[1] ? '' : undefined}
              >
                <title>{`${shortTime(b.t)} — ${b.total.toLocaleString('en-US')} events`}</title>
              </rect>
            ))}
          </>
        ) : (
          marks.map((b, i) => (
            <rect
              key={b.t}
              className="q-tl-bar"
              x={i * bw + 0.5}
              y={H - 2 - yOf(b.total)}
              width={Math.max(0.5, bw - 1)}
              height={yOf(b.total)}
              data-selected={sel && i >= sel[0] && i <= sel[1] ? '' : undefined}
            >
              <title>{`${shortTime(b.t)} — ${b.total.toLocaleString('en-US')} events`}</title>
            </rect>
          ))
        )}
        {sel ? (
          <rect className="q-tl-brush" x={sel[0] * bw} y={0} width={(sel[1] - sel[0] + 1) * bw} height={H} />
        ) : null}
      </svg>
      <div className="q-tl-axis">
        <span>{shortTime(marks[0]!.t)}</span>
        <span className="q-tl-hint">drag to zoom</span>
        <span>{shortTime(marks[n - 1]!.t + sliceMs)}</span>
      </div>
      {stack ? (
        <ul className="agg-legend q-tl-legend" aria-label="Timeline series">
          {stack.series.map((s, i) => (
            <li key={s} className="agg-legend-item">
              <span className="agg-swatch" style={{ background: seriesColor(i) }} />
              <span className="agg-legend-v mono">{s}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
